import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { UserFullname } from '@r/components/user-fullname.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import type { PerformanceCollection } from '@admin/components/performances/performances-columns.tsx';
import { useApiAdminperformancesGetCollection } from '@r/api/generated/client.ts';
import type { PerformancePerformanceCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type CompanyPerformanceMovementsTableProps = {
  companyUniqueCode: string;
};

export function CompanyPerformanceMovementsTable({
  companyUniqueCode
}: CompanyPerformanceMovementsTableProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const columns = useMemo<ColumnDef<PerformanceCollection>[]>(() => {
    return [
      {
        id: 'amount',
        header: t('label.company.performances.amount'),
        cell: ({ row }) => <FormattedNumber value={row.original.amount ?? 0} />
      },
      {
        id: 'label',
        header: t('label.company.performance.pattern'),
        cell: ({ row }) => row.original.label ?? ''
      },
      {
        id: 'realizedAt',
        header: t('label.company.performances.realization.date'),
        cell: ({ row }) =>
          row.original.realizedAt
            ? format(new Date(row.original.realizedAt), 'dd/MM/yyyy', { locale: fr })
            : ''
      },
      {
        id: 'user',
        header: t('label.company.performances.import.user.fullname'),
        cell: ({ row }) => (
          <UserFullname
            fullname={row.original.addedByUserWorkspace?.user?.fullname ?? ''}
            isDeleted={row.original.addedByUserWorkspace?.user?.isDeleted}
          />
        )
      },
      {
        id: 'createdAt',
        header: t('label.company.performances.import.date'),
        cell: ({ row }) =>
          row.original.createdAt
            ? format(new Date(row.original.createdAt), 'dd/MM/yyyy', { locale: fr })
            : ''
      }
    ];
  }, [t]);

  const performancesQuery = useApiAdminperformancesGetCollection(
    {
      'realizedByCompany.uniqueCode': companyUniqueCode,
      page: apiPagination.pageIndex + 1,
      perPage: apiPagination.pageSize,
      'order[realizedAt]': 'desc',
      'order[id]': 'desc'
    },
    {
      query: {
        enabled: Boolean(companyUniqueCode)
      }
    }
  );
  useOrvalQueryErrorToast(performancesQuery, t('toast.error.label'));

  const data = useMemo<PerformanceCollection[]>(() => {
    const performances =
      (performancesQuery.data?.data as PerformancePerformanceCollectionReadModel[]) ?? [];

    return performances.map((performance) => ({
      id: performance.id,
      amount: performance.amount ?? null,
      label: (performance as unknown as { label?: string | null })?.label ?? null,
      realizedAt: performance.realizedAt ?? null,
      createdAt: performance.createdAt ?? null,
      realizedBy: {
        fullname:
          (performance.realizedBy as unknown as { user?: { fullname?: string | null } })?.user
            ?.fullname ?? null
      },
      realizedByCompany: {
        name: performance.realizedByCompany?.name,
        uniqueCode: performance.realizedByCompany?.uniqueCode
      },
      logicTypeTarget: {
        reference: performance.logicTypeTarget?.reference ?? null
      },
      referenceUniqueCode: performance.referenceUniqueCode ?? null,
      referenceQuantity: performance.referenceQuantity ?? null,
      addedByUserWorkspace: performance.addedByUserWorkspace
        ? {
            user: {
              fullname: performance.addedByUserWorkspace.user?.fullname ?? null,
              isDeleted: performance.addedByUserWorkspace.user?.isDeleted
            }
          }
        : null
    }));
  }, [performancesQuery.data]);

  const showTypology = useMemo(
    () =>
      data.some(
        (row) => row.logicTypeTarget?.reference && row.logicTypeTarget.reference !== 'DEFAULT'
      ),
    [data]
  );

  const columnsWithTypology = useMemo<ColumnDef<PerformanceCollection>[]>(() => {
    if (!showTypology) return columns;

    const typologyColumn: ColumnDef<PerformanceCollection> = {
      id: 'typology',
      header: t('label.company.performances.target.reference'),
      cell: ({ row }: { row: { original: PerformanceCollection } }) =>
        row.original.logicTypeTarget?.reference ?? ''
    };

    return [...columns.slice(0, 3), typologyColumn, ...columns.slice(3)];
  }, [columns, showTypology, t]);

  const mappedTable = useDataTable<PerformanceCollection, unknown>({
    data,
    columns: columnsWithTypology,
    pageCount: performancesQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  const { pageIndex, pageSize } = mappedTable.table.getState().pagination;

  useEffect(() => {
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  const isLoading = performancesQuery.isLoading || performancesQuery.isFetching;

  if (isLoading) {
    return (
      <DataTableSkeleton
        columnCount={columnsWithTypology.length}
        cellWidths={['14rem', '12rem', '12rem', '10rem', '10rem']}
      />
    );
  }

  return (
    <DataTable
      table={mappedTable.table}
      totalItems={performancesQuery.data?.pagination?.totalItems}
    />
  );
}
